import type { StrategyThresholds } from '../../utils/financialCalculations';
import { capBasisCaption } from '../../utils/scheduleAnchors';

export interface ScoreRow {
  key: string;
  label: string;
  threshold: number;
  actual: number;
  pass: boolean;
  format: 'percent' | 'ratio';
  /**
   * False when the capital structure makes the metric meaningless - an all-cash deal has no
   * debt to cover, so DSCR is NOT APPLICABLE rather than failed.
   *
   * ⭐⭐ THE ROW IS STILL RETURNED, and that is the point. It used to be dropped, so an all-cash
   * scorecard rendered four rows while Joseph's delivered PDFs render five with the DSCR line
   * reading "N/A  UNLEVERED". Verified against all six analyses of 2026-08-26: every one prints
   * five rows, and the three all-cash deals (Bank of the Sierra, Mojave, Guitar Center) print
   * DSCR as UNLEVERED. An inapplicable metric must never read as a miss.
   */
  applicable: boolean;
}

/**
 * The cap row's label is NOT fixed: on a lease-up deal the cap is the STABILISED one and the row
 * says so (KK3). It is resolved per call, through the same rule every other surface uses.
 */
const METRIC_META: Array<{ key: keyof StrategyThresholds; label: string; format: 'percent' | 'ratio' }> = [
  { key: 'cap_rate', label: '', format: 'percent' },
  { key: 'cash_on_cash', label: 'Avg Cash-on-Cash', format: 'percent' },
  { key: 'irr', label: 'IRR', format: 'percent' },
  { key: 'equity_multiple', label: 'Equity Multiple', format: 'ratio' },
  { key: 'dscr', label: 'DSCR', format: 'ratio' },
];

/**
 * The house verdict. Four states, matching `docs/INVESTMENT-ANALYSIS-HOUSE-STYLE.md` and the
 * banner Joseph's delivered analyses actually print.
 *
 * ⚠️ REPLACED `GO | REVIEW | NO GO` on 2026-08-26. REVIEW collapsed two findings his documents
 * keep apart: a deal whose numbers are clean but has something unresolved (CONDITIONAL) and a
 * deal that misses a metric (CAUTION). Measured across the six delivered that day, the old rule
 * disagreed with him twice - it called STIIIZY GO where he wrote CONDITIONAL, and Guitar Center
 * REVIEW where he wrote NO GO.
 */
export type Verdict = 'GO' | 'CONDITIONAL' | 'CAUTION' | 'NO GO';

/**
 * THE single verdict rule, derived from the six specimens rather than invented.
 *
 *   0 misses, nothing outstanding  -> GO           his definition, word for word
 *   0 misses, open items listed    -> CONDITIONAL  STIIIZY: 5 of 5 and still not a GO
 *   1 miss                         -> CAUTION      Banc, Bank of the Sierra, Mojave
 *   2 or more misses               -> NO GO        Guitar Center (2 of 4), Kiddie (1 of 5)
 *
 * ⭐ `openItems` IS THE MECHANISM, not a flag beside it. GO reads "all applicable metrics clear
 * and no material items outstanding" - so an EMPTY LIST is literally the second half of that
 * sentence. Nothing to maintain in parallel and nothing that can drift out of step with what the
 * banner prints as its reason.
 *
 * ⚠️ The old rule let two misses scrape into REVIEW via a `passed >= ceil(total/2)` floor. It no
 * longer can. That is the one band that got STRICTER, and it is deliberate: Guitar Center misses
 * IRR and equity multiple, and Joseph called it NO GO.
 *
 * ⛔ Defect #8 is why this function is the ONLY scorer. `InvestmentAnalysis.tsx` once kept a
 * parallel metric list and its own majority rule, so one screen showed GO on the verdict card and
 * REVIEW on the decision card for the same deal. `pdfExport.ts` held a third copy. Anything that
 * needs a verdict reads this.
 */
export function scoreDeal(
  metrics: Record<string, number>,
  thresholds: StrategyThresholds,
  suppressed: string[],
  openItems: string[] = [],
  /** Months of lease-up from the close; decides whether the cap row reads in-place or stabilised. */
  captions: { leaseUpMonths?: number | null } = {}
): { rows: ScoreRow[]; passed: number; total: number; verdict: Verdict } {
  const rows: ScoreRow[] = [];
  const capCaption = capBasisCaption(captions.leaseUpMonths);

  METRIC_META.forEach(m => {
    const threshold = thresholds[m.key];
    // A null threshold means the profile does not gate this metric at all, so there is no row
    // to print. That is different from suppression, which prints the row as UNLEVERED.
    if (threshold === null || threshold === undefined) return;
    const applicable = !suppressed.includes(m.key as string);
    const actual = metrics[m.key as string] ?? 0;
    rows.push({
      key: m.key as string,
      label: m.key === 'cap_rate' ? capCaption.goingInCap : m.label,
      threshold,
      actual,
      pass: applicable && actual >= threshold,
      format: m.format,
      applicable,
    });
  });

  const scored = rows.filter(r => r.applicable);
  const passed = scored.filter(r => r.pass).length;
  const total = scored.length;

  return { rows, passed, total, verdict: verdictFrom(passed, total, openItems) };
}

/**
 * The rule itself, as one exported function.
 *
 * ⛔⛔ `pdfExport.ts` CALLS THIS RATHER THAN KEEPING ITS OWN COPY, and that is not tidiness - it
 * is the guardrail Joseph named. The PDF must print the SAME verdict as the screen; one document
 * with two renderers is a defect this build has already paid for (defect #8, where the card said
 * GO and the table said REVIEW on one deal, and pdfExport held a THIRD copy of the majority
 * rule). There is now exactly one place a verdict can come from.
 */
export const verdictFrom = (passed: number, total: number, openItems: string[] = []): Verdict => {
  const misses = total - passed;
  const outstanding = openItems.filter(i => i && i.trim() !== '').length;
  return total === 0 ? 'NO GO'
    : misses === 0 ? (outstanding > 0 ? 'CONDITIONAL' : 'GO')
    : misses === 1 ? 'CAUTION'
    : 'NO GO';
};

/**
 * The definitions Joseph prints in small italic type under the banner. Kept here beside the rule
 * that assigns them, so the words on the page and the logic can never say different things -
 * the copy-versus-maths defect this build has already paid for once ("average" on screen where
 * the function took the median).
 */
export const VERDICT_DEFINITIONS: Record<Verdict, string> = {
  GO: 'all applicable metrics clear and no material items outstanding',
  CONDITIONAL: 'metrics clear, subject to verification of the open items listed',
  CAUTION: 'one or more metrics miss, or open items affect the base case',
  'NO GO': 'metrics miss materially and the gap cannot be closed at a reasonable basis',
};

/**
 * Does this verdict print amber?
 *
 * ⭐ BOTH middle states do. His palette gives CONDITIONAL and CAUTION the same border, fill and
 * text (`C7860B` / `FBF3E2` / `B9770A`) - they differ in MEANING, not in colour. One helper
 * because the colour branch was written out six times in `InvestmentAnalysis.tsx` alone, and six
 * copies of a rule is how this build produces the defect it produces most often.
 */
export const isAmberVerdict = (v: Verdict): boolean => v === 'CONDITIONAL' || v === 'CAUTION';
